import {
  ACTIONPLAN_SECTIONS,
  ACTIONPLAN_SECTION_OTHER,
  ACTIONPLAN_SECTION_REFLECTION,
  ACTIONPLAN_ASSESSMENT_TEMPLATE_SLUG,
  ACTIONPLAN_LAYOUT_TEMPLATE_SLUG,
  ACTIONPLAN_ATTENTION_POINTS,
} from "actionplan/constants";
import type {
  TeacherActionplanBatchDetailResource,
  TeacherActionplanBatchResource,
  TeacherActionplanResource,
  TeacherActionplanShallowResource,
} from "api/generated";
import type {
  Actionplan,
  ActionplanAssessment,
  ActionplanBatch,
  ActionplanBatchDetail,
  ActionplanAssessmentRole,
  ActionplanDetail,
  ActionplanQuestion,
  ActionplanQuestionSection,
  StudentActionplanFormData,
  StudentActionplanPayload,
} from "actionplan/types";
import { toActionplanDeadline } from "actionplan/utils";
import { formatLongDate, toDutchWeekdayString } from "shared/utils/dateHelpers";

// Through the shared helper, so a deadline reads in the school's timezone: a
// row and the page it opens have to name the same day, whatever the browser is
// set to.
const formatDeadline = (deadline: string | null): string | null =>
  deadline === null ? null : formatLongDate(deadline);

export const actionplanFromDto = (
  dto: TeacherActionplanShallowResource,
): Actionplan => ({
  id: dto.id,
  status: dto.status,
  title: dto.title ?? null,
  type: dto.type,
  createdByStudent: dto.created_by_student,
  studentCreatedAt: dto.student_created_at,
  studentPassed: dto.student_passed ?? null,
  teacherPassed: dto.teacher_passed ?? null,
  completedAt: dto.teacher_assessed_at ?? null,
  requestedBy: dto.requested_by ?? null,
  editable: dto.editable,
  deletable: dto.deletable,
  subjectShortage: dto.subject_shortage,
  subjectName: dto.subject_name ?? null,
  schoolyear: dto.schoolyear,
  deadline: formatDeadline(dto.deadline),
  deadlineAt: dto.deadline,
  student: {
    id: dto.student.id,
    fullName: dto.student.full_name,
    mentorClass: dto.student.mentor_class,
    profileImage: dto.student.profile_image,
  },
  createdAt: dto.created_at,
});

export const actionplanBatchFromDto = (
  dto: TeacherActionplanBatchResource,
): ActionplanBatch => ({
  batchId: dto.batch_id,
  actionplanId: dto.actionplan_id ?? null,
  total: dto.total,
  finished: dto.finished,
  status: dto.status,
  title: dto.title ?? null,
  type: dto.type,
  createdByStudent: dto.created_by_student,
  studentCreatedAt: dto.student_created_at,
  teacherPassed: dto.teacher_passed ?? null,
  requestedBy: dto.requested_by ?? null,
  subjectShortage: dto.subject_shortage ?? null,
  subjectName: dto.subject_name ?? null,
  schoolyear: dto.schoolyear,
  deadline: formatDeadline(dto.deadline ?? null),
  deadlineAt: dto.deadline ?? null,
  mentorClasses: dto.mentor_classes,
  students: dto.students.map((student) => ({
    id: student.id,
    fullName: student.full_name,
    profileImage: student.profile_image,
  })),
});

export const actionplanBatchDetailFromDto = (
  dto: TeacherActionplanBatchDetailResource,
): ActionplanBatchDetail => {
  const deadlineAt = dto.deadline ?? null;

  return {
    batchId: dto.batch_id,
    total: dto.total,
    finished: dto.finished,
    status: dto.status,
    title: dto.title ?? null,
    type: dto.type,
    createdByStudent: dto.created_by_student,
    requestedBy: dto.requested_by ?? null,
    subjectShortage: dto.subject_shortage ?? null,
    subjectName: dto.subject_name ?? null,
    schoolyear: dto.schoolyear,
    deadline: deadlineAt === null ? null : formatLongDate(deadlineAt),
    deadlineAt,
    // A deadline is something to plan around, so the batch page offers the
    // weekday behind the date rather than spending the line on it.
    deadlineWeekday:
      deadlineAt === null ? null : toDutchWeekdayString(deadlineAt),
    mentorClasses: dto.mentor_classes,
    requestEditable: dto.request_editable,
    requestDeletable: dto.request_deletable,
    // Sorted here rather than in SQL: student names are encrypted at rest, so
    // the database cannot order on them.
    actionplans: dto.actionplans
      .map(actionplanFromDto)
      .sort((left, right) =>
        left.student.fullName.localeCompare(right.student.fullName, "nl"),
      ),
  };
};

// A picked option is stored by id on imported v1/v2 plans and by label on the
// ones saved here, so the label is looked up and the raw value is the fallback.
// `text` wins: it is what the student typed into an "other" option.
//
// The picks read as one sentence, "Gedrag, motivatie en samenwerken": the
// template labels are lowercased, the student's own text stays as typed, and
// only a template label at the start of the sentence is capitalised.
const answerToText = (
  question: TeacherActionplanResource["questions"][number],
): string => {
  const answer = question.answer;

  if (answer === null) {
    return "";
  }

  if (Array.isArray(answer)) {
    const labels = answer.map(
      (picked) =>
        picked.text ??
        (
          question.options.find((option) => option.id === picked.value)
            ?.value ?? picked.value
        ).toLowerCase(),
    );

    // "a, b en c" — the conjunction comes from the locale, not from a literal.
    const sentence = new Intl.ListFormat("nl-NL", {
      style: "long",
      type: "conjunction",
    }).format(labels);

    if (answer[0]?.text !== null && answer[0]?.text !== undefined) {
      return sentence;
    }

    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  }

  return answer;
};

const RATING_FACE_COUNT = 4;

const toRating = (
  question: TeacherActionplanResource["questions"][number],
): ActionplanDetail["questions"][number]["rating"] => {
  if (question.question_type !== "rating") {
    return null;
  }

  const max = question.options.length || RATING_FACE_COUNT;
  const answer = typeof question.answer === "string" ? question.answer : null;

  if (answer === null || answer === "") {
    return { value: null, max };
  }

  // Rating answers are stored as the face itself, 1-4, matching what
  // RatingListCheckin emits and highlights on; "0" is a legacy plan whose
  // student never answered, and highlights nothing. Passing the value through is
  // the point (ACTIONPLAN-RATING-ANSWERS-ARE-ONE-BASED-001) — a shift here would
  // put every legacy plan one face off.
  const numeric = Number(answer);

  if (Number.isInteger(numeric)) {
    return { value: numeric, max };
  }

  // Fallback: match the answer against option value/id when options exist.
  const index = question.options.findIndex(
    (option) => option.value === answer || option.id === answer,
  );

  return { value: index === -1 ? null : index + 1, max };
};

export const actionplanDetailFromDto = (
  dto: TeacherActionplanResource,
): ActionplanDetail => ({
  id: dto.id,
  status: dto.status,
  studentPassed: dto.student_passed ?? null,
  studentExplanation: dto.student_explanation ?? null,
  studentAssessedAt: dto.student_assessed_at ?? null,
  teacherPassed: dto.teacher_passed ?? null,
  teacherExplanation: dto.teacher_explanation ?? null,
  teacherAssessedAt: dto.teacher_assessed_at ?? null,
  title: dto.title ?? null,
  type: dto.type,
  createdByStudent: dto.created_by_student,
  studentCreatedAt: dto.student_created_at,
  requestedBy: dto.requested_by ?? null,
  editable: dto.editable,
  deletable: dto.deletable,
  canAssess: dto.can_assess,
  subjectShortage: dto.subject_shortage,
  subjectName: dto.subject_name ?? null,
  schoolyear: dto.schoolyear,
  deadline: dto.deadline,
  createdAt: dto.created_at,
  templateName: dto.template_name,
  // v1 and v2 predate the two independent verdicts, so a plan on either has no
  // assessments for the fly-out to show.
  supportsAssessment: dto.template_slug === ACTIONPLAN_ASSESSMENT_TEMPLATE_SLUG,
  usesFrontendLayout: dto.template_slug === ACTIONPLAN_LAYOUT_TEMPLATE_SLUG,
  student: {
    id: dto.student.id,
    fullName: dto.student.full_name,
    mentorClass: dto.student.mentor_class,
    profileImage: dto.student.profile_image,
  },
  questions: dto.questions.map((question) => ({
    id: question.id,
    questionKey: question.question_key,
    section: question.section,
    question: question.question,
    displayLabel: question.display_label,
    questionType: question.question_type,
    options: question.options.map((option) => ({
      id: option.id,
      value: option.value,
      tooltip: option.tooltip,
      optionOther: option.option_other,
    })),
    answer: answerToText(question),
    selectedOptions: Array.isArray(question.answer) ? question.answer : [],
    rating: toRating(question),
  })),
  chats: dto.chats,
});

/**
 * Flattens the detail's two sets of assessment columns into one list. The
 * student's and the teacher's verdicts are independent; each is locked once
 * given, so only `assessAs`'s own pending verdict is editable.
 *
 * The teacher's verdict waits on the student's hand-in: the student's own
 * verdict is what moves the plan to `pending_review`, and before that the
 * endpoint refuses a teacher assessment
 * (@invariant ACTIONPLAN-STUDENT-GRADES-FIRST-001). So the teacher's buttons
 * only appear on a plan that was handed in, and `awaitingStudent` tells the
 * fly-out to say what it is waiting for instead of offering a form that 403s.
 *
 * Whose verdict it is at all comes from `canAssess`: a teacher who may read the
 * plan and reply to it is not necessarily the one who judges it
 * (ACTIONPLAN-VERDICT-FOLLOWS-THE-PLANS-OWNER-001), so they see the verdict as
 * still to be given rather than a form of their own.
 */
export const actionplanAssessmentsFromDetail = (
  detail: ActionplanDetail | undefined,
  assessAs: ActionplanAssessmentRole | null,
): ActionplanAssessment[] => {
  const handedIn = detail?.status === "pending_review";

  // The states the plan is still the student's to write and hand in.
  const withTheStudent =
    detail?.status === "requested" || detail?.status === "active";

  return (
    [
      {
        role: "student",
        passed: detail?.studentPassed ?? null,
        explanation: detail?.studentExplanation ?? null,
        assessedAt: detail?.studentAssessedAt ?? null,
      },
      {
        role: "teacher",
        passed: detail?.teacherPassed ?? null,
        explanation: detail?.teacherExplanation ?? null,
        assessedAt: detail?.teacherAssessedAt ?? null,
      },
    ] as const
  ).map((verdict) => {
    const isOwnPendingVerdict =
      assessAs === verdict.role && verdict.passed === null;

    if (verdict.role === "teacher") {
      const isTheirVerdict = isOwnPendingVerdict && detail?.canAssess === true;

      return {
        ...verdict,
        editable: isTheirVerdict && handedIn,
        awaitingStudent: isTheirVerdict && withTheStudent,
      };
    }

    return {
      ...verdict,
      editable: isOwnPendingVerdict,
      awaitingStudent: false,
    };
  });
};

/**
 * Groups questions by section, preserving the first-seen order.
 *
 * "Huidige status" asks how things went in a subject, so a plan without a
 * subject is never asked it and has nothing to show — the section is dropped
 * rather than rendered empty, and its questions are dropped with it so they
 * cannot resurface in the trailing untitled group.
 */
export const actionplanQuestionSections = (
  questions: ActionplanQuestion[],
  usesFrontendLayout = true,
  isSubjectSpecific = true,
): ActionplanQuestionSection[] => {
  // A legacy plan was written against another template, so its own order and
  // wording are all there is: one untitled list, straight from the rows.
  if (usesFrontendLayout === false) {
    return questions.length === 0
      ? []
      : [{ key: ACTIONPLAN_SECTION_OTHER, questions }];
  }

  const sections = ACTIONPLAN_SECTIONS.filter(
    (section) =>
      isSubjectSpecific === true ||
      section.key !== ACTIONPLAN_SECTION_REFLECTION,
  );

  const droppedKeys = ACTIONPLAN_SECTIONS.filter(
    (section) => sections.includes(section) === false,
  ).flatMap((section) => section.questionKeys as readonly string[]);

  const layoutQuestions = questions.filter(
    (question) => droppedKeys.includes(question.questionKey) === false,
  );

  const groups: ActionplanQuestionSection[] = [];
  const grouped = new Set<number>();

  for (const section of sections) {
    const sectionQuestions = section.questionKeys
      .map((questionKey) =>
        layoutQuestions.find(
          (question) => question.questionKey === questionKey,
        ),
      )
      .filter(
        (question): question is ActionplanQuestion => question !== undefined,
      );

    sectionQuestions.forEach((question) => grouped.add(question.id));

    if (sectionQuestions.length > 0) {
      groups.push({ key: section.key, questions: sectionQuestions });
    }
  }

  // Anything the layout does not name keeps the order the API sent it in.
  const rest = layoutQuestions.filter((question) => !grouped.has(question.id));

  if (rest.length > 0) {
    groups.push({ key: ACTIONPLAN_SECTION_OTHER, questions: rest });
  }

  return groups;
};

/**
 * Maps the student form to the create/update payload. Attention points are sent
 * as `choices`, mirroring the template's `choices` question.
 */
export const studentActionplanPayload = (
  formData: StudentActionplanFormData,
): StudentActionplanPayload => ({
  type: formData.type,
  subject_shortage:
    formData.type === "subject" ? formData.subjectShortage : null,
  deadline: toActionplanDeadline(formData.deadlineDate, formData.deadlineTime),
  answers: {
    class_rating: formData.classRating,
    homework_rating: formData.homeworkRating,
    learn_rating: formData.learnRating,
    motivation_rating: formData.motivationRating,
    choices: ACTIONPLAN_ATTENTION_POINTS.filter((attentionPoint) =>
      formData.attentionPointIds.includes(attentionPoint.id),
    ).map((attentionPoint) => ({
      id: attentionPoint.id,
      text: attentionPoint.optionOther
        ? formData.attentionPointOtherText
        : null,
    })),
    how: formData.how,
    period: formData.period,
    need: formData.need,
  },
});
